import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';

import ConfirmationBasicDialog, {
  ConfirmationBasicDialogProps,
} from '../components/ConfirmationBasicDialog/ConfirmationBasicDialog';

export type ConfirmationOptions = Pick<
  ConfirmationBasicDialogProps,
  | 'titleText'
  | 'titleElements'
  | 'contentText'
  | 'contentElements'
  | 'cancelButtonText'
  | 'approveButtonText'
  | 'contentComponent'
  | 'maxWidth'
  | 'fullWidth'
  | 'approveButtonColor'
>;

interface ConfirmationContextType {
  confirm: (options?: ConfirmationOptions) => Promise<boolean>;
}

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(undefined);

export const useConfirmation = (): ConfirmationContextType => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirmation must be used within a ConfirmationProvider');
  }
  return context;
};

interface ConfirmationProviderProps {
  children: ReactNode;
}

export const ConfirmationProvider: React.FC<ConfirmationProviderProps> = ({ children }) => {
  const [state, setState] = useState<{
    isOpen: boolean;
    resolve?: (value: boolean) => void;
    options: ConfirmationOptions;
  }>({
    isOpen: false,
    options: {},
  });

  const confirm = useCallback((options: ConfirmationOptions = {}): Promise<boolean> => {
    return new Promise(resolve => {
      setState(prevState => {
        if (prevState.isOpen && prevState.resolve) {
          prevState.resolve(false);
        }
        return { isOpen: true, resolve, options };
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (state.resolve) {
      state.resolve(true);
    }
    setState({ isOpen: false, resolve: undefined, options: {} });
  }, [state]);

  const handleReject = useCallback(() => {
    if (state.resolve) {
      state.resolve(false);
    }
    setState({ isOpen: false, resolve: undefined, options: {} });
  }, [state]);

  const handleClose = useCallback(() => {
    handleReject();
  }, [handleReject]);

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      <ConfirmationBasicDialog
        isOpen={state.isOpen}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        handleReject={handleReject}
        {...state.options}
      />
    </ConfirmationContext.Provider>
  );
};
